import { Build, LanguageOutlined, ScienceOutlined } from '@mui/icons-material';
import { Box, Chip, Stack, SvgIconOwnProps, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';

import { RESEARCH_CREDITS } from '../../redux/consts';
import { ToolName } from '../../Types/enums';
import { deepResearchToolsTooltipMessage } from '../../util/messageUtils';
import { handleToolDelete, ToolConfig } from '../../util/tools';
import { useAuth } from '../Providers/AuthProvider';

export interface ChatToolsProps {
  tools: ToolName[];
  disabledTools: ToolName[];
  updateTools: (toolName: ToolName[]) => void;
}
interface ChatTool {
  id: ToolName;
  label: string;
  icon: React.ReactElement;
}

export const getToolIcon = (toolName: ToolName, sx?: SvgIconOwnProps['sx']): React.ReactElement => {
  switch (toolName) {
    case ToolName.WEB_SEARCH:
    case ToolName.GLEAN_SEARCH:
      return <LanguageOutlined sx={sx} />;
    case ToolName.DEEP_RESEARCH:
      return <ScienceOutlined sx={sx} />;
    default:
      return <Build sx={sx} />;
  }
};

const ChatTools: React.FC<ChatToolsProps> = ({ tools, disabledTools, updateTools }) => {
  const { currentUser } = useAuth();

  const getTooltipMessage = (tool: ToolName) => {
    if (
      tool === ToolName.DEEP_RESEARCH &&
      currentUser?.researchCredits !== undefined &&
      currentUser?.researchCredits <= RESEARCH_CREDITS
    ) {
      return `${currentUser.researchCredits} out of ${RESEARCH_CREDITS} Deep Research credits remaining.`;
    }

    return deepResearchToolsTooltipMessage(tool, tools);
  };

  const chatTools: ChatTool[] = useMemo(
    () =>
      tools.map((toolName) => ({
        id: toolName,
        label: ToolConfig[toolName].name,
        icon: (
          <Box
            component="span"
            sx={{
              '& svg': { color: '#070154' },
            }}
          >
            {getToolIcon(toolName, { width: '16px', height: '16px' })}
          </Box>
        ),
      })),
    [tools]
  );

  return (
    <Stack direction="row" spacing={1}>
      {chatTools.map((tool) => (
        <Tooltip key={`${tool.id}-tooltip`} placement="top" title={getTooltipMessage(tool.id)}>
          <div>
            <Chip
              key={tool.id}
              icon={tool.icon}
              label={tool.label}
              onDelete={handleToolDelete(tool.id, tools, updateTools)}
              variant="outlined"
              disabled={disabledTools.includes(tool.id)}
              sx={{
                backgroundColor: '#0047FF33',
                border: '1px solid #0047FF',
                borderRadius: '16px',
                fontWeight: 500,
                px: 1,
                height: '24px',
                '& .MuiChip-deleteIcon': {
                  color: '#070154',
                  width: '16px',
                  height: '16px',
                },
              }}
            />
          </div>
        </Tooltip>
      ))}
    </Stack>
  );
};

export default ChatTools;
